import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { superAdminApi } from '../../services/api';
import { formatDate } from '../../utils/dateFormatter';

interface TenantSummary {
    id: string;
    name: string;
    slug: string;
    region?: string;
    employeeCount: number;
    maxEmployees?: number;
    subscription?: {
        status: string;
        planName?: string;
        trialEndsAt?: string;
    };
    createdAt: string;
}

export default function CompaniesListPage() {
    const [tenants, setTenants] = useState<TenantSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    useEffect(() => {
        loadTenants();
    }, []);

    async function loadTenants() {
        try {
            const data = await superAdminApi.listTenants();
            setTenants(data.tenants || []);
        } catch (error) {
            console.error('Failed to load tenants:', error);
        } finally {
            setLoading(false);
        }
    }

    const filteredTenants = tenants.filter((tenant) => {
        const matchesSearch = !searchTerm ||
            tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tenant.slug.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' ||
            tenant.subscription?.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return <div className="text-center py-8">Loading companies...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Companies</h1>
                <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors">
                    + Invite Company
                </button>
            </div>

            {/* Filters */}
            <div className="bg-gray-800 rounded-lg p-4 mb-6 flex gap-4 flex-wrap">
                <input
                    type="text"
                    placeholder="Search companies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 flex-1 min-w-[200px]"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-purple-500"
                >
                    <option value="all">All Statuses</option>
                    <option value="trial">Trial</option>
                    <option value="active">Active</option>
                    <option value="grace">Grace Period</option>
                    <option value="readonly">Read-only</option>
                    <option value="expired">Expired</option>
                </select>
            </div>

            {/* Table */}
            <div className="bg-gray-800 rounded-lg overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-700">
                        <tr className="text-left text-gray-300 text-sm">
                            <th className="px-4 py-3">Company</th>
                            <th className="px-4 py-3">Region</th>
                            <th className="px-4 py-3">Users</th>
                            <th className="px-4 py-3">Plan</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Trial Ends</th>
                            <th className="px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                        {filteredTenants.map((tenant) => (
                            <tr key={tenant.id} className="hover:bg-gray-700/50">
                                <td className="px-4 py-3">
                                    <div>
                                        <p className="font-medium">{tenant.name}</p>
                                        <p className="text-sm text-gray-400">{tenant.slug}</p>
                                    </div>
                                </td>
                                <td className="px-4 py-3">{tenant.region || 'US'}</td>
                                <td className="px-4 py-3">
                                    <span className={tenant.employeeCount >= (tenant.maxEmployees || 10) ? 'text-red-400' : ''}>
                                        {tenant.employeeCount} / {tenant.maxEmployees || 10}
                                    </span>
                                </td>
                                <td className="px-4 py-3">{tenant.subscription?.planName || '-'}</td>
                                <td className="px-4 py-3">
                                    <StatusBadge status={tenant.subscription?.status || 'trial'} />
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-400">
                                    {tenant.subscription?.trialEndsAt
                                        ? formatDate(tenant.subscription.trialEndsAt)
                                        : '-'}
                                </td>
                                <td className="px-4 py-3">
                                    <Link
                                        to={`/super-admin/companies/${tenant.id}`}
                                        className="text-purple-400 hover:text-purple-300 text-sm"
                                    >
                                        Manage
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredTenants.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                        No companies found
                    </div>
                )}
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const styles: Record<string, string> = {
        active: 'bg-green-600 text-green-100',
        trial: 'bg-yellow-600 text-yellow-100',
        grace: 'bg-orange-600 text-orange-100',
        readonly: 'bg-red-600 text-red-100',
        expired: 'bg-gray-600 text-gray-100',
    };

    return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.trial}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
    );
}
